import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, shopApi, getShopSession, fileUrl } from "@/lib/api";
import { useShopIdleLogout } from "@/hooks/useShopIdleLogout";
import { ShopHeader } from "@/components/ShopHeader";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Save, ChevronLeft, Clock, Pencil, Trash2 } from "lucide-react";

function fmtTHB(n) { return `฿${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmtTHBn(n) { return `฿${Math.round(Number(n || 0)).toLocaleString("en-US")}`; }

function EntryEditDialog({ open, onOpenChange, entry, staff, services, slug, onSaved }) {
  const { t } = useLang();
  const [staffId, setStaffId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [time24, setTime24] = useState("");
  const [duration, setDuration] = useState(60);
  const [discount, setDiscount] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !entry) return;
    setStaffId(entry.staff_id);
    setServiceId(entry.service_id || "");
    setTime24(entry.time_24h);
    setDuration(entry.duration_mins);
    setDiscount(Number(entry.discount_thb || 0));
  }, [open, entry]);

  const svcObj = useMemo(() => services.find(s => s.id === serviceId), [services, serviceId]);
  const staffObj = useMemo(() => staff.find(s => s.id === staffId), [staff, staffId]);
  const previewPrice = svcObj ? Math.round(svcObj.price_per_60min * duration / 60 * 100) / 100 : (entry?.price_thb || 0);
  const discNum = Math.max(0, Math.min(Number(discount || 0), previewPrice));
  const previewCommission = staffObj ? Math.round((previewPrice - discNum) * staffObj.commission_rate * 100) / 100 : 0;

  const submit = async () => {
    if (!entry) return;
    setBusy(true);
    try {
      await shopApi(slug).put(`/shops/${slug}/service-entries/${entry.id}`, {
        staff_id: staffId,
        service_id: serviceId || null,
        time_24h: time24,
        duration_mins: Number(duration),
        discount_thb: Number(discount || 0),
      });
      toast.success("Entry updated");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="entry-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Correct entry</DialogTitle>
          <DialogDescription>Fix staff, service, time, or duration — totals recalculate automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="caption-label">{t("input.staff_working_name")}</Label>
            <Select value={staffId} onValueChange={setStaffId}>
              <SelectTrigger data-testid="entry-edit-staff"><SelectValue placeholder="Staff" /></SelectTrigger>
              <SelectContent>
                {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.working_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="caption-label">{t("common.service")}</Label>
            <Select value={serviceId} onValueChange={setServiceId}>
              <SelectTrigger data-testid="entry-edit-service"><SelectValue placeholder="Service" /></SelectTrigger>
              <SelectContent>
                {services.map(s => <SelectItem key={s.id} value={s.id}>{s.name} — {fmtTHBn(s.price_per_60min)}/60m</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="caption-label">{t("common.time")}</Label>
              <Input type="time" value={time24} onChange={(e) => setTime24(e.target.value)} data-testid="entry-edit-time" />
            </div>
            <div>
              <Label className="caption-label">{t("common.duration")}</Label>
              <Input type="number" min="1" step="1" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="entry-edit-duration" />
            </div>
          </div>
          <div>
            <Label className="caption-label">{t("common.discount")}</Label>
            <Input type="number" min="0" step="1" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="entry-edit-discount" />
          </div>
          <div className="rounded-md border border-stone-200 bg-stone-50 p-3 grid grid-cols-2 gap-3">
            <div>
              <div className="caption-label">New price</div>
              <div className="font-mono tabular text-base mt-0.5" data-testid="entry-edit-preview-price">{fmtTHBn(previewPrice)}</div>
            </div>
            <div>
              <div className="caption-label">New commission</div>
              <div className="font-mono tabular text-base mt-0.5" data-testid="entry-edit-preview-commission">{fmtTHB(previewCommission)}</div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !staffId || !serviceId} className="bg-emerald-800 hover:bg-emerald-900" data-testid="entry-edit-submit">
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ShopInput() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t } = useLang();
  const [shop, setShop] = useState(null);
  const [staff, setStaff] = useState([]);
  const [workingIds, setWorkingIds] = useState(null); // null = not yet loaded
  const [services, setServices] = useState([]);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const session = getShopSession(slug);
  const canEdit = ["owner", "manager"].includes(session?.role || "");

  const bkkNow = new Date(Date.now() + 420 * 60000); // UTC + 7h, read via getUTC* for BKK wall-clock
  const hh = String(bkkNow.getUTCHours()).padStart(2, "0");
  const mm = String(bkkNow.getUTCMinutes()).padStart(2, "0");
  // Business day defers to the shop's opening/closing config (overnight-aware).
  // Falls back to BKK calendar when the shop payload hasn't landed yet.
  const bkkToday = shop?.business_date_today || bkkNow.toISOString().slice(0, 10);
  const [time24, setTime24] = useState(`${hh}:${mm}`);
  const [duration, setDuration] = useState(60);
  const [discount, setDiscount] = useState(0);
  const [staffId, setStaffId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const isStaff = session?.role === "staff";

  const loadAll = async () => {
    try {
      const [shopR, stR, svR, enR, attR] = await Promise.all([
        axios.get(`${API}/shops/by-slug/${slug}`),
        shopApi(slug).get(`/shops/${slug}/staff`),
        shopApi(slug).get(`/shops/${slug}/services`),
        shopApi(slug).get(`/shops/${slug}/service-entries`, { params: { date: bkkToday } }),
        shopApi(slug).get(`/shops/${slug}/attendance`, { params: { date: bkkToday } }),
      ]);
      setShop(shopR.data);
      setStaff(stR.data.staff.filter(s => s.is_enabled));
      setServices(svR.data.services);
      setEntries(enR.data.entries);
      setWorkingIds(new Set(attR.data.staff.filter(s => s.is_working).map(s => s.id)));
      // Staff can only log services under their own name — pre-select and lock the dropdown.
      if (session?.role === "staff" && session?.person_id) {
        setStaffId(session.person_id);
      }
    } catch (e) {
      if (e.response?.status === 401) nav(`/${slug}`);
      else toast.error("Failed to load data");
    }
  };

  const reloadEntries = async () => {
    try {
      const { data } = await shopApi(slug).get(`/shops/${slug}/service-entries`, { params: { date: bkkToday } });
      setEntries(data.entries);
    } catch {}
  };

  useEffect(() => {
    const sess = getShopSession(slug);
    if (!sess?.token) { nav(`/${slug}`); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Auto-logout after 20s of inactivity on every shop screen — drops any unsaved data.
  useShopIdleLogout(slug, t("shop.idle_signout"));

  const staffObj = useMemo(() => staff.find(s => s.id === staffId), [staff, staffId]);
  const svcObj = useMemo(() => services.find(s => s.id === serviceId), [services, serviceId]);
  const calcPrice = useMemo(() => svcObj ? Math.round(svcObj.price_per_60min * duration / 60 * 100) / 100 : 0, [svcObj, duration]);
  const discNum = useMemo(() => Math.max(0, Math.min(Number(discount || 0), calcPrice)), [discount, calcPrice]);
  const calcCommission = useMemo(() => staffObj ? Math.round((calcPrice - discNum) * staffObj.commission_rate * 100) / 100 : 0, [staffObj, calcPrice, discNum]);

  const submit = async (e) => {
    e.preventDefault();
    if (!staffObj || !svcObj) return toast.error("Select staff and service");
    setBusy(true);
    try {
      await shopApi(slug).post(`/shops/${slug}/service-entries`, {
        staff_id: staffId,
        service_id: serviceId,
        time_24h: time24,
        duration_mins: Number(duration),
        discount_thb: Number(discount || 0),
      });
      toast.success(t("input.saved"));
      setServiceId(""); setDuration(60); setDiscount(0);
      await reloadEntries();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const deleteEntry = async (e) => {
    if (!window.confirm(`Delete entry at ${e.time_24h} for ${e.staff_working_name}?`)) return;
    try {
      await shopApi(slug).delete(`/shops/${slug}/service-entries/${e.id}`);
      toast.success("Entry deleted");
      reloadEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen shop-bg">
      <ShopHeader shop={shop} showSignOut />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <button onClick={() => nav(`/${slug}`)} className="caption-label flex items-center gap-1 mb-2 text-stone-500 hover:text-stone-800" data-testid="input-back-link">
              <ChevronLeft className="w-3 h-3" /> back
            </button>
            <h1 className="font-serif text-4xl text-stone-900">{t("input.title")}</h1>
          </div>
          <div className="font-mono text-lg tabular text-stone-700 hidden sm:flex items-center gap-2">
            <Clock className="w-4 h-4 text-emerald-800" /> {time24}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="border-stone-200/80">
            <CardContent className="p-6">
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="caption-label">{t("input.staff_working_name")}</Label>
                  {isStaff ? (
                    <div
                      className="mt-1 flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-900"
                      data-testid="input-staff-locked"
                    >
                      <span className="font-medium">{session?.person}</span>
                      <span className="caption-label text-emerald-700">{t("input.your_entries_only")}</span>
                    </div>
                  ) : (() => {
                    // Owner / manager see only staff on today's roster. If nobody
                    // has been ticked yet, show a hint pointing them back to Home.
                    const working = workingIds
                      ? staff.filter(s => workingIds.has(s.id))
                      : staff;
                    if (workingIds && working.length === 0) {
                      return (
                        <div
                          className="mt-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                          data-testid="input-no-working-staff"
                        >
                          No staff ticked as working today. Tick a name on the Home page roster first.
                        </div>
                      );
                    }
                    return (
                      <Select value={staffId} onValueChange={setStaffId}>
                        <SelectTrigger data-testid="input-staff-select"><SelectValue placeholder={t("common.select_staff")} /></SelectTrigger>
                        <SelectContent>
                          {working.map(s => <SelectItem key={s.id} value={s.id} data-testid={`input-staff-option-${s.working_name}`}>{s.working_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </div>
                <div>
                  <Label className="caption-label">{t("common.service")}</Label>
                  <Select value={serviceId} onValueChange={setServiceId}>
                    <SelectTrigger data-testid="input-service-select"><SelectValue placeholder={t("common.select_service")} /></SelectTrigger>
                    <SelectContent>
                      {services.map(s => <SelectItem key={s.id} value={s.id} data-testid={`input-service-option-${s.id}`}>{s.name} — {fmtTHBn(s.price_per_60min)}/60m</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="caption-label">{t("common.time")}</Label>
                    <Input type="time" value={time24} onChange={(e) => setTime24(e.target.value)} required data-testid="input-time" />
                  </div>
                  <div>
                    <Label className="caption-label">{t("common.duration")}</Label>
                    <Input type="number" min="1" step="1" value={duration} onChange={(e) => setDuration(e.target.value)} required data-testid="input-duration" />
                  </div>
                </div>
                <div>
                  <Label className="caption-label">{t("common.discount")}</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    placeholder="0"
                    data-testid="input-discount"
                  />
                </div>
                <div className="rounded-md border border-emerald-100 bg-emerald-50/60 p-4 grid grid-cols-2 gap-4">
                  <div>
                    <div className="caption-label">{t("common.price")}</div>
                    <div className="font-mono tabular text-lg mt-1" data-testid="input-calc-price">{fmtTHBn(calcPrice)}</div>
                  </div>
                  <div>
                    <div className="caption-label">{t("input.staff_earnings")}</div>
                    <div className="font-mono tabular text-lg mt-1" data-testid="input-calc-commission">{fmtTHB(calcCommission)}</div>
                  </div>
                </div>
                <Button type="submit" disabled={busy || !staffId || !serviceId} className="w-full bg-emerald-800 hover:bg-emerald-900" data-testid="service-input-save-button">
                  <Save className="w-4 h-4 mr-1.5" /> {t("input.save")}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-stone-200/80">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif text-xl">{t("input.recent_today")}</h2>
                <span className="caption-label">{entries.length} entries</span>
              </div>
              <div className="max-h-[520px] overflow-y-auto">
                {entries.length === 0 ? (
                  <div className="text-sm text-stone-500 text-center py-8">{t("input.no_entries_today")}</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left border-b border-stone-200">
                      <tr>
                        <th className="py-2 caption-label">{t("common.time")}</th>
                        <th className="py-2 caption-label">{t("input.staff_working_name")}</th>
                        <th className="py-2 caption-label">{t("common.service")}</th>
                        <th className="py-2 caption-label text-right">Mins</th>
                        <th className="py-2 caption-label text-right">{t("common.price")}</th>
                        {canEdit && <th className="py-2 caption-label text-right w-16">·</th>}
                      </tr>
                    </thead>
                    <tbody data-testid="input-entries-body">
                      {entries.map(e => (
                        <tr key={e.id} className="border-b border-stone-100" data-testid={`input-entry-${e.id}`}>
                          <td className="py-2 font-mono">{e.time_24h}</td>
                          <td className="py-2">{e.staff_working_name}</td>
                          <td className="py-2">{e.service_name}</td>
                          <td className="py-2 text-right font-mono tabular">{e.duration_mins}</td>
                          <td className="py-2 text-right font-mono tabular">{fmtTHBn(e.price_thb)}</td>
                          {canEdit && (
                            <td className="py-1 text-right whitespace-nowrap">
                              <Button size="sm" variant="ghost" onClick={() => setEditing(e)} title="Correct entry" data-testid={`entry-edit-button-${e.id}`}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteEntry(e)} title="Delete entry" data-testid={`entry-delete-button-${e.id}`}>
                                <Trash2 className="w-3.5 h-3.5 text-red-600" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {canEdit && entries.length > 0 && (
                <p className="text-xs text-stone-400 mt-2" data-testid="input-corrections-hint">
                  Owner/Manager can edit or delete mistyped entries — reports update instantly.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <EntryEditDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        entry={editing}
        staff={staff}
        services={services}
        slug={slug}
        onSaved={reloadEntries}
      />
    </div>
  );
}
